import { useMutation } from '@apollo/client';
import { SpinnerButton, TextInput } from '@do/walrus';
import { Form, Formik } from 'formik';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import copy from '../copy';
import {
  resetPassword as ResetPasswordRequest,
  resetPasswordVariables
} from '../generated-types/resetPassword';
import { RESET_PASSWORD } from '../queries/queries';
import { handleFormError } from '../utils/gql-utils';
import { yupPasswordValidation } from '../utils/validation-utils';
import { ErrorBanner } from './Common';
import NewErrorBanner from './newBrandDesign/ErrorBanner';
import NewSpinnerButton from './newBrandDesign/SpinnerButton';
import NewTextInput from './newBrandDesign/TextInput';

const passwordResetValidation = Yup.object().shape({
  password: yupPasswordValidation
});

interface Props {
  token: string;
  isNewBrandDesignEnabled: boolean;
}

const PasswordResetForm: React.FC<Props> = ({
  token,
  isNewBrandDesignEnabled
}) => {
  const TextInputComponent = isNewBrandDesignEnabled ? NewTextInput : TextInput;
  const ErrorBannerComponent = isNewBrandDesignEnabled
    ? NewErrorBanner
    : ErrorBanner;
  const SpinnerButtonComponent = isNewBrandDesignEnabled
    ? NewSpinnerButton
    : SpinnerButton;

  const [resetPassword, { loading: isLoading }] = useMutation<
    ResetPasswordRequest,
    resetPasswordVariables
  >(RESET_PASSWORD);

  const [genericServerError, setGenericServerError] = React.useState(null);
  const navigate = useNavigate();

  return (
    <Formik
      initialValues={{
        password: ''
      }}
      validationSchema={passwordResetValidation}
      onSubmit={(values, { setErrors }) => {
        setGenericServerError(null); /* Reset displayed error */
        resetPassword({
          variables: {
            resetPasswordRequest: { password: values.password, token }
          }
        })
          .then(() => {
            navigate('/login', {
              state: { success: 'reset' }
            });
          })
          .catch(handleFormError(setErrors, setGenericServerError));
      }}
    >
      {({ errors, values, touched }) => (
        <Form>
          {genericServerError && (
            <ErrorBannerComponent error={genericServerError} />
          )}

          <TextInputComponent
            type="password"
            placeholder={copy.form.password_reset.label}
            name="password"
            required
            label={copy.form.password_reset.placeholder}
            touched={touched.password}
            error={errors.password}
            value={values.password}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
          />
          <SpinnerButtonComponent
            className={isNewBrandDesignEnabled ? '' : 'do-u-mt--medium'}
            type="submit"
            fullWidth
            spinning={isLoading}
            disabled={isLoading}
            loadingMessage={copy.form.password_reset.loading}
          >
            {copy.form.password_reset.button_text}
          </SpinnerButtonComponent>
        </Form>
      )}
    </Formik>
  );
};

export default PasswordResetForm;
